import { useRef, useState } from 'react';
import style from '../styles/VenueCard.module.css';
import { trpc } from '../utils/trpc';
import GroupInfo from '../page-components/GroupInfo';
import ReactDOM from 'react-dom';

interface GroupCardProps {
  id: string;
}

/**
 * A template component to render individual group overviews
 * Accepts a group id as a prop to determine subject
 * @returns Individual group overview card
 */
export function GroupCard({ id }: GroupCardProps) {
  // Setup use references
  const cardRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const { data: groupQuery, isLoading } = trpc.group.getById.useQuery({ id: id });
  const { data: members = [] } = trpc.group.getMembers.useQuery(
    { groupId: id! },
    { enabled: !!id },
  );

  // Initialize banner image reference
  const bannerUrl = groupQuery?.banner_url;

  // Initialize group tags
  const tags =
    groupQuery?.group_tags?.map((t: { tags: { name: string } }) => t.tags.name) ?? [];

  // Initialize default avatar
  const avatarInitial = groupQuery?.name?.charAt(0).toUpperCase() ?? 'G';

  // Initialize member count
  const resolvedMemberCount = groupQuery?.group_members?.[0]?.count ?? 0;

  // Initialize group admins
  const adminList = members
    .filter((m) => m.role === 'owner' || m.role === 'admin' || m.role === 'mod')
    .map((m) => {
      const profile = Array.isArray(m.member_profiles)
        ? m.member_profiles[0]
        : m.member_profiles;
      return {
        name: profile?.username ?? 'Unknown',
        avatar: profile?.username?.charAt(0).toUpperCase() ?? '?',
        role: (m.role === 'owner' ? 'Owner' : m.role === 'mod' ? 'Mod' : 'Admin') as
          | 'Owner'
          | 'Admin'
          | 'Mod',
      };
    });

  // Dynamic styles for the background banner
  const dynamicBackgroundStyle = bannerUrl
    ? {
        backgroundImage: `linear-gradient(to right, #2D4F2B 20%, transparent 100%), url(${bannerUrl})`,
        backgroundSize: '100%, cover',
        backgroundPosition: '0 0, center',
      }
    : {};

  // Standard display
  return isLoading ? (
    // Loading placeholder
    <div className={style.skeletonCard}>
      <div className={style.skeletonTitle}></div>
      <div className={style.skeletonText}></div>
    </div>
  ) : (
    // Successful retrieval
    <div
      ref={cardRef}
      className={style.venueCardContainer}
      onClick={() => setOpen(true)}
      style={dynamicBackgroundStyle}
    >
      {/* Group name display */}
      <h2>{groupQuery?.name}</h2>

      {/* Group member count display */}
      <h3>
        <i>{groupQuery?.group_members?.[0]?.count ?? 0} member(s)</i>
      </h3>

      {/* Expanded information modal */}
      {open &&
        ReactDOM.createPortal(
          <GroupInfo
            group={{
              id: id,
              name: groupQuery.name,
              handle: groupQuery.name?.toLowerCase().replace(/\s+/g, '-'),
              avatar: avatarInitial,
              description: groupQuery.description ?? '',
              memberCount: resolvedMemberCount,
              postCount: groupQuery.postCount ?? 0,
              founded: groupQuery.created_at
                ? new Date(groupQuery.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric',
                  })
                : '—',
              tags,
              isJoined: false,
              bannerUrl: groupQuery.banner_url ?? undefined,
              profile_picture_url: groupQuery.profile_picture_url ?? undefined,
              rules: [],
              admins: adminList,
            }}
            onClose={() => setOpen(false)}
          />,
          document.body,
        )}
    </div>
  );
}
